import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux'
import API from '../../../utils/API';
import MesTable from '../../../components/Table/MesTable';
import constants from '../../../utils/constants';
import commonMethods from '../../../utils/common-methods';
import commonFunctions from '../../../utils/commonFunctions';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import { FormattedMessage, useIntl } from 'react-intl';
import MesReceiversModal from '../../../components/MessageManager/MesReceiversModal';

const MESSENGER_TEXT_PREFIX = "MESSENGER_";

function MessageHistoryTable(props) {
  const { updateFlag, onLoadHistoryTable } = props;
  const intl = useIntl();
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('dateAndTime');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalData, setModalData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const [historyData, setHistoryData] = useState([]);

  const loadHistoryTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getHistories(50);
      res.data.forEach((mess) => {
        map.data[mess.id] = {
          id: mess.id,
          message: mess.title,
          type: mess.format,
          groups: mess.groups.map((role) => role.name).join(', '),
          dateAndTime: commonMethods.formatDate(mess.timeStart),
          status: intl.formatMessage({ id: MESSENGER_TEXT_PREFIX + mess.status }),
          checked: false,
          receivers: mess.failedReceivers,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setDataCount(map.ids.length);
      setHistoryData(res.data);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (err) {
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502)
        return; 
      setError(err.message);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadHistoryTable) {
      onLoadHistoryTable(() => {
        loadHistoryTable(dataMap)
      });
    }
  }, [onLoadHistoryTable]);

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    loadHistoryTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, historyData]);

  useEffect(() => {
    loadHistoryTable(dataMap);
  }, [updateFlag]);

  function handleViewReceiversClick(receivers) {
    setModalData(receivers);
  }

  const tableColumnArray = [
    {
      Header: 'MESSAGE',
      id: 'message',
      accessor: 'message',
      isVisible: true,
    },
    {
      Header: 'TYPE',
      id: 'type',
      accessor: 'type',
      isVisible: true,
    },
    {
      Header: 'GROUPS',
      id: 'groups',
      isVisible: true,
      accessor: 'groups',
    },
    {
      Header: 'DATE_AND_TIME',
      id: 'dateAndTime',
      accessor: 'dateAndTime',
      isVisible: true,
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      isVisible: true,
    },
    {
      Header: 'VIEW_RECEIVERS',
      id: 'mesViewReceivers',
      Cell: ({ row }) => (
        <div
          type="button"
          className="link-button"
          data-bs-toggle="modal"
          data-bs-target="#mesReceiversModal"
          onClick={() => handleViewReceiversClick(row.original.receivers)}
        >
          <FormattedMessage id="VIEW_RECEIVERS" />
        </div>
      ),
      isVisible: false,
    }
  ];

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbmm_history_table",
    pageType: constants.pageType.MessageHistory,
    modalId: "Status",
    columns: tableColumnArray,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: '288px',
  };

  return (
    <div>
      <MesReceiversModal
        receivers={modalData}
        onClose={() => setModalData([])}
      />
      <MesTable {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(MessageHistoryTable);
